/* eslint-disable @typescript-eslint/no-explicit-any */
import { Blob } from '@consigli/types';

import { ModalAlert, EditModal, TextInput } from '@/components';

import { useDocumentsContext } from '../context/documents-context';

interface DeleteModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  blobToDelete: Blob | null;
  findingsToDelete: number;
  isDeleting: boolean;
}

export const DeleteModal = ({
  isOpen,
  onOpenChange,
  onConfirm,
  blobToDelete,
  findingsToDelete,
  isDeleting,
}: DeleteModalProps) => (
  <ModalAlert
    isOpen={isOpen}
    onOpenChange={onOpenChange}
    onConfirm={onConfirm}
    title="Delete Document"
    description={
      blobToDelete
        ? `Are you sure you want to delete the document "${blobToDelete.name}"?${
            findingsToDelete > 0
              ? ` Deleting this document will also delete ${findingsToDelete} findings associated with it.`
              : ''
          } This action cannot be undone.`
        : ''
    }
    confirmText="Delete"
    cancelText="Cancel"
    isLoading={isDeleting}
    loadingText="Deleting..."
    destructive={true}
  />
);

// Context-based version of DeleteModal
export const DeleteModalWithContext = () => {
  const {
    isModalOpen,
    blobToDelete,
    findingsToDelete,
    isDeleting,
    handleCloseModal,
    handleConfirmDelete,
  } = useDocumentsContext();

  return (
    <ModalAlert
      isOpen={isModalOpen}
      onOpenChange={handleCloseModal}
      onConfirm={handleConfirmDelete}
      title="Delete Document"
      description={
        blobToDelete
          ? `Are you sure you want to delete the document "${blobToDelete.name}"?${
              findingsToDelete > 0
                ? ` Deleting this document will also delete ${findingsToDelete} findings associated with it.`
                : ''
            } This action cannot be undone.`
          : ''
      }
      confirmText="Delete"
      cancelText="Cancel"
      isLoading={isDeleting}
      loadingText="Deleting..."
      destructive={true}
    />
  );
};

interface EditFilenameModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  itemToEdit: any;
  newBaseName: string;
  onBaseNameChange: (value: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isUpdating: boolean;
}

export const EditFilenameModal = ({
  isOpen,
  onOpenChange,
  itemToEdit,
  newBaseName,
  onBaseNameChange,
  onConfirm,
  onCancel,
  isUpdating,
}: EditFilenameModalProps) => (
  <EditModal
    isOpen={isOpen}
    onOpenChange={onOpenChange}
    title="Edit Filename"
    description="Enter a new name for the document (extension will be preserved)."
    data={newBaseName}
    onDataChange={onBaseNameChange}
    onConfirm={onConfirm}
    onCancel={onCancel}
    isSubmitting={isUpdating}
    submitLabel="Save"
    cancelLabel="Cancel"
    isValid={newBaseName.trim().length > 0}
  >
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 min-w-0">
        <TextInput
          value={newBaseName}
          onChange={(e) => onBaseNameChange(e.target.value)}
          placeholder="Enter filename"
          fullWidth
        />
      </div>
      {itemToEdit && (
        <span className="text-sm text-gray-500 whitespace-nowrap flex-shrink-0">
          {(() => {
            if ('name' in itemToEdit) {
              const lastDotIndex = itemToEdit.name.lastIndexOf('.');
              return lastDotIndex === -1 ? '' : itemToEdit.name.substring(lastDotIndex);
            } else {
              const lastDotIndex = itemToEdit.filename.lastIndexOf('.');
              return lastDotIndex === -1 ? '' : itemToEdit.filename.substring(lastDotIndex);
            }
          })()}
        </span>
      )}
    </div>
  </EditModal>
);

// Context-based version of EditFilenameModal
export const EditFilenameModalWithContext = () => {
  const {
    isEditModalOpen,
    itemToEdit,
    newBaseName,
    isUpdating,
    handleCloseEditModal,
    handleConfirmEdit,
    setNewBaseName,
  } = useDocumentsContext();

  return (
    <EditModal
      isOpen={isEditModalOpen}
      onOpenChange={handleCloseEditModal}
      title="Edit Filename"
      description="Enter a new name for the document (extension will be preserved)."
      data={newBaseName}
      onDataChange={setNewBaseName}
      onConfirm={handleConfirmEdit}
      onCancel={handleCloseEditModal}
      isSubmitting={isUpdating}
      submitLabel="Save"
      cancelLabel="Cancel"
      isValid={newBaseName.trim().length > 0}
    >
      <div className="flex items-center gap-2 w-full">
        <div className="flex-1 min-w-0">
          <TextInput
            value={newBaseName}
            onChange={(e) => setNewBaseName(e.target.value)}
            placeholder="Enter filename"
            fullWidth
          />
        </div>
        {itemToEdit && (
          <span className="text-sm text-gray-500 whitespace-nowrap flex-shrink-0">
            {(() => {
              const item = itemToEdit as any;
              if ('name' in item) {
                const lastDotIndex = item.name.lastIndexOf('.');
                return lastDotIndex === -1 ? '' : item.name.substring(lastDotIndex);
              } else if ('filename' in item) {
                const lastDotIndex = item.filename.lastIndexOf('.');
                return lastDotIndex === -1 ? '' : item.filename.substring(lastDotIndex);
              }
              return '';
            })()}
          </span>
        )}
      </div>
    </EditModal>
  );
};
